import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  correctAttendanceEvent,
  createAttendancePolicyVersion,
  getEffectiveTardiness,
  listAttendanceCorrections,
  listAttendanceEvents,
  listAttendancePolicies,
  listEmployees,
  listPlannedSchedules,
  listTardiness,
  recordAttendanceEvent,
} from '../../api/hr';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  Button,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  ListToolbar,
  LoadingNote,
  PageHeader,
  Select,
  SummaryStrip,
  TextInput,
  formatDateTime,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
  Pagination,
} from '../../components/common';
import type { AttendanceCorrection, AttendanceEvent, EffectiveTardiness, Employee, Schedule, Tardiness } from '../../types/domain';

/** ISO timestamp -> value for an <input type="datetime-local"> in the browser's local timezone. */
function toDatetimeLocalValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Attendance workspace (FASE 9, migration 0040). Branch-scoped —
 * re-fetches on Cabang Aktif change. Lateness ("Terlambat") is the real
 * `tardiness` table only, computed server-side by fn_record_attendance_event
 * against attendance_policies.late_tolerance_minutes — never derived or
 * estimated here. Recording an event requires an existing PLANNED
 * schedule (no schedule-authoring UI in this slice — schedules are seeded
 * upstream by a workforce-planning capability not yet built).
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md NEEDS_DECISION log.
 */
export default function AttendancePage() {
  const { hasPermission, context } = usePermissions();
  const currentUserId = context && context.authenticated ? context.user_id : null;
  const { activeBranchId } = useBranch();
  const [events, setEvents] = useState<AttendanceEvent[]>([]);
  const [tardiness, setTardiness] = useState<Tardiness[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [hasPolicy, setHasPolicy] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showPolicyForm, setShowPolicyForm] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');

  const canCreate = hasPermission('attendance_event', 'create');
  const canSetPolicy = hasPermission('attendance_policy', 'create');
  const canCorrect = hasPermission('attendance_event', 'correct');
  const [correctingEvent, setCorrectingEvent] = useState<AttendanceEvent | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [eventRows, tardyRows, empRows, allPolicyRows] = await Promise.all([
        listAttendanceEvents(activeBranchId ?? undefined),
        listTardiness(activeBranchId ?? undefined),
        listEmployees(activeBranchId ?? undefined),
        listAttendancePolicies(),
      ]);
      setEvents(eventRows);
      setTardiness(tardyRows);
      setEmployees(empRows);
      // Mirrors fn_record_attendance_event's own fallback: a branch-specific
      // ACTIVE policy, or (if none) a tenant-wide one (branch_id null).
      setHasPolicy(allPolicyRows.some((p) => p.status === 'ACTIVE' && (p.branch_id === activeBranchId || p.branch_id === null)));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId]);

  const employeeName = (id: string) => employees.find((e) => e.employee_id === id)?.legal_name ?? id;
  const tardinessByEmployee = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of tardiness) m.set(t.employee_id, (m.get(t.employee_id) ?? 0) + 1);
    return m;
  }, [tardiness]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return events.filter((e) => {
      if (q && !employeeName(e.employee_id).toLowerCase().includes(q)) return false;
      if (typeFilter !== 'ALL' && e.event_type !== typeFilter) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, search, typeFilter, employees]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 20);

  return (
    <div>
      <PageHeader title="Kehadiran" subtitle="Riwayat clock-in/clock-out — keterlambatan dihitung dari kebijakan nyata. Migrasi 0040." />
      {hasPolicy === false && (
        <ErrorNote text="Belum ada kebijakan keterlambatan (attendance policy) aktif untuk cabang ini — pencatatan clock-in akan ditolak sampai kebijakan dibuat." />
      )}
      {canSetPolicy && (
        <Button variant="secondary" onClick={() => setShowPolicyForm((v) => !v)} style={{ marginBottom: 12 }} disabled={!activeBranchId}>
          {showPolicyForm ? 'Batal' : 'Atur Kebijakan Keterlambatan'}
        </Button>
      )}
      {showPolicyForm && canSetPolicy && activeBranchId && (
        <SetPolicyForm
          branchId={activeBranchId}
          onSaved={() => {
            setShowPolicyForm(false);
            void load();
          }}
        />
      )}
      {canCreate && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }} disabled={!activeBranchId}>
          {showForm ? 'Batal' : '+ Catat Kehadiran'}
        </Button>
      )}
      {showForm && canCreate && (
        <RecordAttendanceForm
          employees={employees}
          onRecorded={() => {
            setShowForm(false);
            void load();
          }}
        />
      )}
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && (
        <>
          <SummaryStrip
            items={[
              { label: 'Total Event', value: events.length },
              { label: 'Karyawan Terlambat', value: tardinessByEmployee.size, tone: tardinessByEmployee.size > 0 ? ('danger' as const) : ('neutral' as const) },
              { label: 'Total Kejadian Terlambat', value: tardiness.length, tone: tardiness.length > 0 ? ('warning' as const) : ('neutral' as const) },
            ]}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari nama karyawan..."
            resultCount={total}
            filters={
              <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ width: 140 }}>
                <option value="ALL">Semua tipe</option>
                <option value="CLOCK_IN">Masuk</option>
                <option value="CLOCK_OUT">Pulang</option>
              </Select>
            }
          />
          {events.length === 0 && <EmptyState text="Belum ada kehadiran tercatat." />}
          {events.length > 0 && total === 0 && <EmptyState text="Tidak ada event yang cocok dengan pencarian/filter." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Karyawan</th>
                  <th style={thStyle}>Tipe</th>
                  <th style={thStyle}>Waktu</th>
                  {canCorrect && <th style={thStyle}>Aksi</th>}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((e) => (
                  <tr key={e.event_id}>
                    <td style={tdStyle}>{employeeName(e.employee_id)}</td>
                    <td style={tdStyle}>{e.event_type === 'CLOCK_IN' ? 'Masuk' : 'Pulang'}</td>
                    <td style={tdStyle}>{formatDateTime(e.event_time)}</td>
                    {canCorrect && (
                      <td style={tdStyle}>
                        <Button variant="secondary" onClick={() => setCorrectingEvent(e)} style={{ padding: '4px 10px', fontSize: 'var(--alex-fs-micro)' }}>
                          Koreksi
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}
      <CorrectionDrawer
        event={correctingEvent}
        open={!!correctingEvent}
        onClose={() => setCorrectingEvent(null)}
        employeeName={correctingEvent ? employeeName(correctingEvent.employee_id) : ''}
        currentUserId={currentUserId}
        onCorrected={() => {
          setCorrectingEvent(null);
          void load();
        }}
      />
    </div>
  );
}

const textareaStyle = {
  display: 'block',
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--alex-border)',
  borderRadius: 'var(--alex-control-radius)',
  fontSize: 'var(--alex-fs-caption)',
  color: 'var(--alex-text-primary)',
  background: 'var(--alex-surface)',
  fontFamily: 'var(--alex-font-family)',
  resize: 'vertical' as const,
  marginTop: 3,
};

/**
 * Attendance fact immutability (migration 0042/0043): attendance_events
 * cannot be edited or deleted directly (trg_attendance_events_no_update
 * blocks it at the DB level). This drawer never mutates the original row —
 * it calls fn_correct_attendance_event_checked, which records a canonical
 * attendance_corrections row (original value snapshot, corrected/effective
 * value, reason, actor) and supersedes any prior correction on the same
 * event. RAW + latest correction = EFFECTIVE — shown below, alongside the
 * effective (recalculated, never rewritten-in-place) lateness result.
 */
function CorrectionDrawer({
  event,
  open,
  onClose,
  employeeName,
  currentUserId,
  onCorrected,
}: {
  event: AttendanceEvent | null;
  open: boolean;
  onClose: () => void;
  employeeName: string;
  currentUserId: string | null;
  onCorrected: () => void;
}) {
  const [correctedAt, setCorrectedAt] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<AttendanceCorrection[] | null>(null);
  const [effectiveTardiness, setEffectiveTardiness] = useState<EffectiveTardiness | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  useEffect(() => {
    setReason('');
    setError(null);
    setHistory(null);
    setEffectiveTardiness(null);
    setLoadErr(null);
    if (!event) return;
    setCorrectedAt(toDatetimeLocalValue(event.event_time));
    Promise.all([listAttendanceCorrections(event.event_id), getEffectiveTardiness(event.event_id)])
      .then(([corrections, tardy]) => {
        setHistory(corrections);
        setEffectiveTardiness(tardy);
      })
      .catch((e) => setLoadErr(e instanceof Error ? e.message : String(e)));
  }, [event?.event_id]);

  if (!event) return null;

  const applied = history?.find((c) => c.status === 'APPLIED') ?? null;
  const effectiveTime = applied ? applied.corrected_event_time : event.event_time;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError('Alasan koreksi wajib diisi.');
      return;
    }
    if (!correctedAt) {
      setError('Waktu terkoreksi wajib diisi.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await correctAttendanceEvent(event.event_id, new Date(correctedAt).toISOString(), reason.trim());
      onCorrected();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title="Koreksi Kehadiran">
      <DrawerField label="Karyawan" value={employeeName} />
      <DrawerField label="Tipe" value={event.event_type === 'CLOCK_IN' ? 'Masuk' : 'Pulang'} />
      <DrawerField label="Original (fakta asli, tidak pernah berubah)" value={formatDateTime(event.event_time)} />
      <DrawerField label="Efektif (dipakai untuk pelaporan/keterlambatan)" value={formatDateTime(effectiveTime)} />
      {loadErr && <ErrorNote text={loadErr} />}
      {effectiveTardiness && (
        <DrawerField
          label="Keterlambatan efektif"
          value={effectiveTardiness.is_late ? `Terlambat ${effectiveTardiness.late_minutes} menit${effectiveTardiness.is_corrected ? ' (setelah koreksi)' : ''}` : `Tidak terlambat${effectiveTardiness.is_corrected ? ' (setelah koreksi)' : ''}`}
        />
      )}

      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--alex-border)' }}>
        <div style={{ fontWeight: 700, fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-primary)', marginBottom: 8 }}>Riwayat Koreksi</div>
        {history === null && <LoadingNote />}
        {history && history.length === 0 && <EmptyState text="Belum pernah dikoreksi." variant="compact" />}
        {history && history.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {history.map((c) => (
              <div key={c.correction_id} className="alex-card" style={{ padding: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <strong style={{ fontSize: 'var(--alex-fs-micro)' }}>
                    {formatDateTime(c.original_event_time)} → {formatDateTime(c.corrected_event_time)}
                  </strong>
                  <span style={{ fontSize: 'var(--alex-fs-micro)', color: c.status === 'APPLIED' ? 'var(--alex-success)' : 'var(--alex-text-faint)' }}>
                    {c.status === 'APPLIED' ? 'Diterapkan' : 'Digantikan'}
                  </span>
                </div>
                <div style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-secondary)' }}>{c.reason}</div>
                <div style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-faint)', marginTop: 4 }}>
                  Diajukan oleh {c.requested_by === currentUserId ? 'Anda' : 'HR/Owner lain'} — {formatDateTime(c.created_at)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--alex-border)' }}>
        <p style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-faint)', margin: '0 0 10px' }}>
          Fakta kehadiran asli tidak dapat diedit/dihapus langsung — sistem menolaknya di level database. Mengajukan koreksi baru mencatat nilai
          efektif baru sebagai dokumen terpisah (bukan menimpa data asli) dan otomatis menggantikan koreksi sebelumnya jika ada. Tidak ada langkah
          persetujuan terpisah — mengajukan koreksi memerlukan izin attendance_event.correct (HR/Owner) sebagai gerbang otorisasi tunggal.
        </p>
        <form onSubmit={handleSubmit}>
          <FieldLabel label="Waktu terkoreksi (nilai efektif baru)">
            <TextInput type="datetime-local" value={correctedAt} onChange={(e) => setCorrectedAt(e.target.value)} required />
          </FieldLabel>
          <div style={{ marginTop: 10 }}>
            <FieldLabel label="Alasan koreksi">
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} required rows={3} style={textareaStyle} />
            </FieldLabel>
          </div>
          <Button type="submit" variant="primary" loading={submitting} style={{ marginTop: 12 }}>
            Ajukan Koreksi
          </Button>
          {error && <ErrorNote text={error} />}
        </form>
      </div>
    </Drawer>
  );
}

function RecordAttendanceForm({ employees, onRecorded }: { employees: Employee[]; onRecorded: () => void }) {
  const [employeeId, setEmployeeId] = useState('');
  const [schedules, setSchedules] = useState<Schedule[] | null>(null);
  const [scheduleId, setScheduleId] = useState('');
  const [eventType, setEventType] = useState<'CLOCK_IN' | 'CLOCK_OUT'>('CLOCK_IN');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSchedules(null);
    setScheduleId('');
    if (!employeeId) return;
    listPlannedSchedules(employeeId)
      .then(setSchedules)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [employeeId]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!scheduleId) {
      setError('Pilih jadwal (schedule) terlebih dahulu.');
      return;
    }
    setSubmitting(true);
    try {
      await recordAttendanceEvent(scheduleId, employeeId, eventType);
      onRecorded();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 180 }}>
        <FieldLabel label="Karyawan">
          <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} required>
            <option value="" disabled>
              Pilih...
            </option>
            {employees.map((emp) => (
              <option key={emp.employee_id} value={emp.employee_id}>
                {emp.employee_no} — {emp.legal_name}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ flex: 1, minWidth: 180 }}>
        <FieldLabel label="Jadwal (schedule)">
          <Select value={scheduleId} onChange={(e) => setScheduleId(e.target.value)} required disabled={!employeeId}>
            <option value="" disabled>
              {employeeId ? 'Pilih...' : 'Pilih karyawan dahulu'}
            </option>
            {schedules?.map((s) => (
              <option key={s.schedule_id} value={s.schedule_id}>
                {s.work_date}
              </option>
            ))}
          </Select>
        </FieldLabel>
        {employeeId && schedules && schedules.length === 0 && (
          <div style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-faint)', marginTop: 4 }}>Tidak ada jadwal PLANNED untuk karyawan ini.</div>
        )}
      </div>
      <div style={{ width: 140 }}>
        <FieldLabel label="Tipe">
          <Select value={eventType} onChange={(e) => setEventType(e.target.value as 'CLOCK_IN' | 'CLOCK_OUT')}>
            <option value="CLOCK_IN">Masuk</option>
            <option value="CLOCK_OUT">Pulang</option>
          </Select>
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Catat
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}

function SetPolicyForm({ branchId, onSaved }: { branchId: string; onSaved: () => void }) {
  const [effectiveFrom, setEffectiveFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [tolerance, setTolerance] = useState('15');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const t = Number(tolerance);
    if (!Number.isFinite(t) || t < 0) {
      setError('Toleransi keterlambatan harus >= 0 menit.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await createAttendancePolicyVersion(branchId, effectiveFrom, t);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-end' }}>
      <div style={{ width: 160 }}>
        <FieldLabel label="Berlaku mulai">
          <TextInput type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} required />
        </FieldLabel>
      </div>
      <div style={{ width: 160 }}>
        <FieldLabel label="Toleransi (menit)">
          <TextInput type="number" min="0" step="1" value={tolerance} onChange={(e) => setTolerance(e.target.value)} required />
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Simpan Kebijakan
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}
